import type { ReactNode } from 'react';
import type { ProblemSeverity } from '../engine/projection';
import { useGlossary } from '../glossary/GlossaryContext';
import { getTerm } from '../glossary/terms';
import { Chip } from './Chip';

/**
 * Larger explanation card for engine-surfaced "things to know about your
 * plan." Lives next to charts / on the dashboard. Tone is friendly,
 * actionable, plain-language — never an alarm.
 *
 * Color discipline mirrors {@link ProblemBadge}:
 *   - `note`     → muted slate background, soft border
 *   - `warning`  → muted amber
 *   - `critical` → warm orange (still calm, never red)
 *
 * Use this in clusters under charts, or one-off in dashboard sections. When
 * showing many at once, prefer to slice the engine-derived list with a
 * sensible cap (e.g., top 3 on the dashboard, top 5 on the projection page).
 */

interface Action {
  label: string;
  href?: string;
  onClick?: () => void;
}

interface Props {
  severity: ProblemSeverity;
  title: ReactNode;
  body?: ReactNode;
  actions?: Action[];
  /** Optional leading glyph; defaults to a calm severity-appropriate marker. */
  icon?: ReactNode;
  /** Glossary term ids referenced in title/body. Rendered as a small
   *  "Terms in this note" chip row so the user can click any to open the
   *  panel — body strings can't carry React components, so jargon is
   *  surfaced here as data instead. */
  termRefs?: string[];
  className?: string;
}

const PANEL_CLS: Record<ProblemSeverity, string> = {
  note:
    'border-ink-200 bg-ink-50/60 dark:border-ink-700 dark:bg-ink-800/40',
  warning:
    'border-amber-500/30 bg-amber-50/70 dark:border-amber-500/30 dark:bg-amber-700/10',
  critical:
    'border-orange-500/30 bg-orange-50/70 dark:border-orange-500/30 dark:bg-orange-700/10',
};

const ICON_CLS: Record<ProblemSeverity, string> = {
  note: 'bg-ink-200 text-ink-600 dark:bg-ink-700 dark:text-ink-300',
  warning: 'bg-amber-200 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300',
  critical: 'bg-orange-200 text-orange-800 dark:bg-orange-500/20 dark:text-orange-300',
};

const DEFAULT_GLYPH: Record<ProblemSeverity, string> = {
  note: 'i',
  warning: '!',
  critical: '!!',
};

export function ProblemPanel({
  severity,
  title,
  body,
  actions,
  icon,
  termRefs,
  className = '',
}: Props) {
  const { open } = useGlossary();
  // Resolve term refs against the live glossary so deleted/typoed ids
  // simply don't render (rather than producing dead chips).
  const resolvedTerms = (termRefs ?? [])
    .map(id => ({ id, def: getTerm(id) }))
    .filter(t => t.def != null);

  return (
    <div
      // We deliberately don't pass `role="alert"` — these are calm,
      // observational panels, not interruptive alerts.
      className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 text-xs ${PANEL_CLS[severity]} ${className}`}
    >
      <span
        aria-hidden
        className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${ICON_CLS[severity]}`}
      >
        {icon ?? DEFAULT_GLYPH[severity]}
      </span>
      <div className="flex-1 space-y-1">
        <div className="text-sm font-semibold text-ink-900 dark:text-ink-50">
          {title}
        </div>
        {body && (
          <div className="text-[12px] leading-relaxed text-ink-600 dark:text-ink-200">
            {body}
          </div>
        )}
        {resolvedTerms.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            <span className="text-[10px] uppercase tracking-wide text-ink-400">
              Terms:
            </span>
            {resolvedTerms.map(({ id, def }) => (
              <Chip
                key={id}
                size="sm"
                tone="muted"
                onClick={() => open(id)}
                title={def!.short}
              >
                {def!.term}
              </Chip>
            ))}
          </div>
        )}
        {actions && actions.length > 0 && (
          <div className="flex flex-wrap gap-3 pt-1">
            {actions.map(a =>
              a.href ? (
                <a
                  key={a.label}
                  href={a.href}
                  className="text-[11px] font-semibold text-accent-700 underline-offset-2 hover:underline dark:text-accent-500"
                >
                  {a.label} →
                </a>
              ) : (
                <button
                  key={a.label}
                  type="button"
                  onClick={a.onClick}
                  className="text-[11px] font-semibold text-accent-700 underline-offset-2 hover:underline dark:text-accent-500"
                >
                  {a.label} →
                </button>
              ),
            )}
          </div>
        )}
      </div>
    </div>
  );
}
